import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import PublicReportHeader from "../../components/diagnostics/PublicReportHeader";
import OwnerAcknowledgmentBlock from "../../components/diagnostics/OwnerAcknowledgmentBlock";
import ReportPdfButton, {
  PrintButton,
} from "../../components/diagnostics/ReportPdfButton";
import { API } from "../../lib/api";

const STATE_LABEL = {
  revoked: "This link is no longer active.",
  expired: "This link has expired.",
  deleted: "The report behind this link has been removed.",
  unfinalized: "This report is being revised. Please check back later.",
};

export default function PublicReportView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use raw axios — no cookies, no Authorization, no withCredentials.
        const r = await axios.get(`${API}/public/reports/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 410 && detail && typeof detail === "object") {
          setError({ kind: detail.error || "expired", message: detail.message });
        } else if (status === 404) {
          setError({
            kind: "not_found",
            message: "We couldn't find that link. Please check the URL.",
          });
        } else {
          setError({
            kind: "unknown",
            message: "Something went wrong loading this report.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) {
    return (
      <PublicShell>
        <div
          data-testid="public-report-error"
          className="rounded-md border border-red-200 bg-red-50 p-6 text-center"
        >
          <div className="font-heading text-lg font-semibold text-red-900">
            {STATE_LABEL[error.kind] || "Link unavailable"}
          </div>
          <p className="text-sm text-red-800 mt-2">{error.message}</p>
        </div>
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <div
          data-testid="public-report-loading"
          className="text-center text-sm text-slate-500 py-12"
        >
          Loading report…
        </div>
      </PublicShell>
    );
  }

  const propertyAddress = data.property
    ? [data.property.address, data.property.city, data.property.state]
        .filter(Boolean)
        .join(", ")
    : null;
  const isOwnerSafe = data.scope === "owner_safe";
  const photos = data.photos || [];

  return (
    <PublicShell>
      <PublicReportHeader
        propertyAddress={propertyAddress}
        finalizedAt={data.finalized_at}
      />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="public-actions">
        <ReportPdfButton tokenUrl={`${API}/public/reports/${token}/pdf`} />
        <PrintButton tokenUrl={`${API}/public/reports/${token}/render`} />
      </div>

      {data.owner_summary && (
        <Section title="Summary">
          <div
            data-testid="public-owner-summary"
            className="rounded-md border-l-4 border-amber-500 bg-amber-50/60 px-5 py-4 text-slate-800 leading-relaxed whitespace-pre-wrap"
          >
            {data.owner_summary}
          </div>
        </Section>
      )}

      {!isOwnerSafe && data.findings_text && (
        <Section title="Findings">
          <p
            data-testid="public-findings"
            className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap"
          >
            {data.findings_text}
          </p>
        </Section>
      )}

      {!isOwnerSafe && data.recommendations_text && (
        <Section title="Recommendations">
          <p
            data-testid="public-recommendations"
            className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap"
          >
            {data.recommendations_text}
          </p>
        </Section>
      )}

      {(data.test_readings || []).length > 0 && (
        <Section title="Test Readings">
          <table
            data-testid="public-readings-table"
            className="w-full text-sm border-collapse"
          >
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
                <th className="py-2 pr-3 font-bold">Reading</th>
                <th className="py-2 pr-3 font-bold">Value</th>
                <th className="py-2 pr-3 font-bold">Unit</th>
              </tr>
            </thead>
            <tbody>
              {data.test_readings.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-slate-800">{r.label}</td>
                  <td className="py-2 pr-3 font-data font-semibold text-slate-900">
                    {r.value}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{r.unit || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {(data.parts_required || []).length > 0 && (
        <Section title="Parts">
          <table
            data-testid="public-parts-table"
            className="w-full text-sm border-collapse"
          >
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-300">
                <th className="py-2 pr-3 font-bold">Part</th>
                <th className="py-2 pr-3 font-bold">Qty</th>
                <th className="py-2 pr-3 font-bold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.parts_required.map((p, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-slate-800">{p.label}</td>
                  <td className="py-2 pr-3 font-data font-semibold text-slate-900">
                    {p.qty || 1}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{p.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {photos.length > 0 && (
        <Section title="Photos">
          <div
            data-testid="public-photo-grid"
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {photos.map((p) => (
              <figure
                key={p.id}
                data-testid={`public-photo-${p.id}`}
                className="border border-slate-200 rounded-md overflow-hidden bg-slate-50"
              >
                <img
                  src={`${API}/public/reports/${token}/photos/${p.id}`}
                  alt={p.caption || "diagnostic photo"}
                  loading="lazy"
                  className="w-full max-h-72 object-cover"
                />
                {p.caption && (
                  <figcaption className="px-3 py-2 text-xs text-slate-600 border-t border-slate-200 bg-white">
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </Section>
      )}

      <OwnerAcknowledgmentBlock token={token} />

      <footer className="mt-16 pt-6 border-t border-slate-200 text-[11px] text-slate-500 flex justify-between">
        <span>Zion Solar Service OS</span>
        <span>Confidential — internal report</span>
      </footer>
    </PublicShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold pb-2 mb-3 border-b border-slate-200">
        {title}
      </h2>
      {children}
    </section>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-md shadow-card border border-slate-200 p-8 sm:p-12">
        {children}
      </div>
    </div>
  );
}
